/**
 * Phase E / Q2 — Per-document provenance footer.
 *
 * Distinct from `ProvenancePanel.tsx` (which surfaces *AI-generated*
 * provenance: source prompt session, model, etc.). This component
 * surfaces *source-of-truth* provenance for ANY document open in the
 * Document Library:
 *   - Source: SharePoint / Vault / Generated
 *   - Last modified (ISO + relative)
 *   - Author / responsible attorney
 *   - "Live" badge when the doc mirrors a real SharePoint file
 *     (vs. a fixture-only doc with no `externalRef`)
 *
 * Mounted inside `DocumentPreview.tsx` below the content area.
 *
 * Plan: ~/.claude/plans/it-s-time-to-rustling-locket.md §"Phase E".
 */

import type { ReactElement } from 'react';

import { testIds } from '../../lib/testIds';

const FONT =
  "var(--xp-font-body)";

export type ProvenanceSource = 'sharepoint' | 'vault' | 'generated' | 'fixture';

export interface DocumentProvenanceProps {
  source: ProvenanceSource;
  /** ISO timestamp from `modifiedAt`. */
  lastModifiedISO?: string | null;
  /** Display name. Pre-resolved by the parent (the parent already
   *  knows the ATTORNEY_NAMES map). */
  authorDisplayName?: string | null;
  /** Web URL into SharePoint when known — flips the "live" badge on. */
  sharePointWebUrl?: string | null;
}

/**
 * Pure label + color mapping for the Source pill. Extracted so vitest
 * can pin behaviour without RTL.
 */
export function sourceBadgeStyle(source: ProvenanceSource): {
  label: string;
  bg: string;
  fg: string;
} {
  switch (source) {
    case 'sharepoint':
      return { label: 'SharePoint', bg: 'var(--xp-selection)', fg: '#ffffff' };
    case 'vault':
      return { label: 'Vault', bg: '#660099', fg: '#ffffff' };
    case 'generated':
      return { label: 'Generated', bg: '#006600', fg: '#ffffff' };
    case 'fixture':
    default:
      return { label: 'Fixture', bg: '#666666', fg: '#ffffff' };
  }
}

function formatTimestamp(iso: string | null | undefined): string {
  if (!iso) return '—';
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return iso;
  const delta = Date.now() - t;
  const abs = new Date(t).toLocaleString();
  if (delta < 0) return abs;
  const mins = Math.floor(delta / 60_000);
  if (mins < 1) return `just now (${abs})`;
  if (mins < 60) return `${mins} min ago (${abs})`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago (${abs})`;
  const days = Math.floor(hours / 24);
  return `${days}d ago (${abs})`;
}

export function DocumentProvenance(
  props: DocumentProvenanceProps,
): ReactElement {
  const badge = sourceBadgeStyle(props.source);
  const isLive =
    props.source === 'sharepoint' &&
    typeof props.sharePointWebUrl === 'string' &&
    props.sharePointWebUrl.length > 0;

  return (
    <div
      data-testid={testIds.documentProvenance.root}
      className="flex flex-wrap items-center gap-3 px-3 py-1.5 border-t border-t-[#808080] bg-[#ece9d8] text-black"
      style={{
        fontFamily: FONT,
        fontSize: 11,
      }}
    >
      <span
        data-testid={testIds.documentProvenance.source}
        className="inline-block px-2 py-0.5 rounded-sm font-bold"
        style={{
          background: badge.bg,
          color: badge.fg,
          fontSize: 10,
        }}
      >
        {badge.label}
      </span>

      {isLive && (
        <span
          data-testid={testIds.documentProvenance.liveBadge}
          title="This document mirrors a live SharePoint file."
          className="inline-block px-2 py-0.5 rounded-sm font-bold"
          style={{
            background: '#008000',
            color: '#ffffff',
            fontSize: 10,
          }}
        >
          ● Live
        </span>
      )}

      <span data-testid={testIds.documentProvenance.lastModified}>
        <strong>Modified:</strong> {formatTimestamp(props.lastModifiedISO)}
      </span>

      <span data-testid={testIds.documentProvenance.author}>
        <strong>Author:</strong>{' '}
        {props.authorDisplayName && props.authorDisplayName.length > 0
          ? props.authorDisplayName
          : '—'}
      </span>

      {isLive && props.sharePointWebUrl && (
        <a
          data-testid={testIds.documentProvenance.webUrl}
          href={props.sharePointWebUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="underline"
          style={{ color: 'var(--xp-selection)' }}
        >
          Open in SharePoint ↗
        </a>
      )}
    </div>
  );
}
